import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, message } from "antd";
import dayjs from "dayjs";
import { UiButton } from "../../ui";
import CgIcon from "../common/CgIcon";
import {
  ListScreen,
  ListHead,
  DistBand,
  ListCard,
  IconAction,
  RowActions,
  RefCell,
  NameCell,
  Tag,
  StatusPill,
  DimCell,
  NumCell,
  pageSizeOf,
  PHARMACY_PAGE_SIZE,
  ListTable,
} from "../common/ListScreen";
import { getTablePagination } from "../../../utils/tablePagination";
import { useBucketCounts } from "../common/bucketCounts";
import {
  getPharmacyPurchaseReturnsApi,
  completePharmacyPurchaseReturnApi,
  cancelPharmacyPurchaseReturnApi,
} from "../../../services/pharmacyPurchaseReturns.services";
import { formatAmount } from "../common/money";
import { canCancelDocument, cancelFailureMessage } from "../common/cancelWindow";
import { useIsPharmacyAdmin } from "../Users/PharmacyUsers";
import { getErrorMessage } from "../../../helpers/error";
import RaisePurchaseReturnModal from "./RaisePurchaseReturnModal";


/*
 * A return is a draft until it is either completed - at which point the stock
 * goes back - or called off. Those are the three states the API counts it in,
 * and the band is built from the same three.
 */
const BUCKETS = [
  { key: "draft", label: "Draft", color: "#EAAA5E" },
  { key: "completed", label: "Completed", color: "#12B76A" },
  { key: "cancelled", label: "Cancelled", color: "#F97066" },
];

const STATUS_TONE = {
  draft: ["#FFFAEB", "#B54708"],
  completed: ["#ECFDF3", "#027A48"],
  cancelled: ["#FEF3F2", "#B42318"],
};

/* A status the server has that this screen does not know about reads as a
   state rather than as an alarm - cancelled is the red one, and a pill that
   borrowed it would say the wrong thing. */
const NEUTRAL_TONE = ["#F2F4F7", "#475467"];

const prettyStatus = (value) =>
  String(value || "")
    .split("_")
    .join(" ")
    .replace(/^./, (c) => c.toUpperCase());

/** The medicines going back - the first one named, the rest counted. */
const medicineSummary = (items = []) => {
  const names = items
    .map((i) => i.pharmacy_medicine_name || i.medicine_name)
    .filter(Boolean);
  if (!names.length) return "-";
  return names.length === 1 ? names[0] : `${names[0]} +${names.length - 1}`;
};

const totalQty = (items = []) =>
  items.reduce((sum, i) => sum + (Number(i.quantity ?? i.return_qty) || 0), 0);

const PurchaseReturnList = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [working, setWorking] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const isPharmacyAdmin = useIsPharmacyAdmin();

  /* Approving or calling a return off changes what the list and the band say
     about it, so the page is read again rather than patched in the browser. */
  const refreshPage = () => setRefresh((prev) => !prev);

  useEffect(() => {
    setLoading(true);
    const queryParams = { page, limit: PHARMACY_PAGE_SIZE };
    if (status) queryParams.status = status;
    if (search) queryParams.search = search;
    getPharmacyPurchaseReturnsApi(new URLSearchParams(queryParams))
      .then((res) => setData(res?.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [page, search, status, refresh]);

  const counts = useBucketCounts({
    buckets: BUCKETS,
    response: data,
    activeKey: status,
    search,
  });

  /*
   * Approving from the row.
   *
   * A draft return is stock that has been written down as going back but has
   * not gone anywhere yet. Approving it completes the return, which is the
   * moment the stock actually leaves the shelf - so the figure and the fact
   * that it cannot be undone are both said before it does.
   */
  const askToApprove = (record) => {
    Modal.confirm({
      title: `Approve ${record.return_number || "this purchase return"}?`,
      content: `${formatAmount(
        record.total_amount
      )} goes back to ${record.vendor_name || "the vendor"} and the stock leaves the shelf. This cannot be undone.`,
      okText: "Approve",
      cancelText: "Not now",
      onOk: () => {
        setWorking(record.id);
        return completePharmacyPurchaseReturnApi(record.id)
          .then(() => {
            message.success(
              `${record.return_number || "Purchase return"} approved. Stock has been reduced.`
            );
            refreshPage();
          })
          .catch((err) => message.error(getErrorMessage(err)))
          .finally(() => setWorking(null));
      },
    });
  };

  /*
   * Calling a return off from the row.
   *
   * The same question the foot of the return itself asks, asked here so a
   * draft raised by mistake does not have to be opened to be called off. Who
   * may cancel what - and until when - is the module's one rule, so the row
   * offers the control exactly where the return screen would offer it.
   */
  const askToCancel = (record) => {
    Modal.confirm({
      title: "Cancel this purchase return?",
      content: "Are you sure? This cannot be undone.",
      okText: "Yes, cancel it",
      okButtonProps: { danger: true },
      cancelText: "No",
      onOk: () => {
        setWorking(record.id);
        return cancelPharmacyPurchaseReturnApi(record.id)
          .then(() => {
            message.success("Purchase return cancelled");
            refreshPage();
          })
          .catch((err) =>
            message.error(cancelFailureMessage(err, getErrorMessage(err)))
          )
          .finally(() => setWorking(null));
      },
    });
  };

  const columns = [
    {
      title: "Return",
      dataIndex: "return_number",
      key: "return_number",
      render: (value) => <RefCell>{value}</RefCell>,
    },
    {
      title: "Vendor",
      key: "vendor_name",
      render: (_, record) => <NameCell name={record.vendor_name} />,
    },
    {
      title: "Medicine",
      key: "medicine",
      render: (_, record) => medicineSummary(record.items),
    },
    {
      title: "Qty",
      key: "qty",
      align: "right",
      render: (_, record) => <NumCell>{totalQty(record.items)}</NumCell>,
    },
    {
      title: "Value",
      dataIndex: "total_amount",
      key: "total_amount",
      align: "right",
      render: (value) => <NumCell>{formatAmount(value)}</NumCell>,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      key: "reason",
      render: (value) => (value ? <Tag>{value}</Tag> : <DimCell>-</DimCell>),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (value) => (
        <StatusPill tone={STATUS_TONE[value] || NEUTRAL_TONE}>
          {prettyStatus(value)}
        </StatusPill>
      ),
    },
    {
      title: "Date",
      dataIndex: "return_date",
      key: "return_date",
      render: (value) => (
        <DimCell>{value ? dayjs(value).format("DD MMM YYYY") : "-"}</DimCell>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "right",
      /*
       * The actions stay against the right edge while the rest of the table
       * scrolls under them. A wide list pushed this column off the end, where
       * its heading read as a cut-off word and its marks could not be reached
       * without scrolling to find them.
       */
      fixed: "right",
      width: 196,
      render: (_, record) => (
        <RowActions>
          {/* Only a draft is waiting on a decision; a completed or cancelled
              return has already had one. */}
          {record.status === "draft" ? (
            <UiButton
              size="sm"
              disabled={working === record.id}
              onClick={() => askToApprove(record)}
            >
              Approve
            </UiButton>
          ) : null}
          <IconAction
            icon="forms"
            label="View purchase return"
            onClick={() => navigate(`/pharmacy/purchase-return/${record.id}`)}
          />
          {/* A return nobody can call off any more gets no cross - a control
              that only ever refuses is worse than one that is not there. */}
          {canCancelDocument(record, { isAdmin: isPharmacyAdmin }) ? (
            <IconAction
              icon="close"
              tone="danger"
              label="Cancel purchase return"
              disabled={working === record.id}
              onClick={() => askToCancel(record)}
            />
          ) : null}
        </RowActions>
      ),
    },
  ];

  const total = Number(data?.count) || 0;

  const segments = BUCKETS.map((b) => ({ ...b, count: counts[b.key] ?? 0 }));
  /* The whole list, which the unfiltered response knows outright; until
     one has come back the segments in hand are the best there is. */
  const bandTotal =
    counts.ALL ?? segments.reduce((sum, s) => sum + s.count, 0);

  return (
    <ListScreen>
      <ListHead
        title="Purchase Returns"
        actions={
          <UiButton onClick={() => setRaiseOpen(true)}>
            <CgIcon name="plus" size={16} stroke={2.2} />
            New Purchase Return
          </UiButton>
        }
      />

      <DistBand
        total={bandTotal}
        label="Purchase Returns"
        segments={segments}
        activeKey={status}
        onSelect={(key) => {
          setPage(1);
          setStatus((prev) => (prev === key ? null : key));
        }}
      />

      <ListCard
        title="Purchase Returns"
        range={{ page, pageSize: pageSizeOf(data, PHARMACY_PAGE_SIZE), total: total }}
        count={total}
        search={{
          value: search,
          onChange: (e) => {
            setSearch(e?.target?.value || "");
            setPage(1);
          },
          placeholder: "Search return, vendor or reason",
        }}
        onClear={
          search || status
            ? () => {
                setSearch("");
                setStatus(null);
                setPage(1);
              }
            : undefined
        }
      >
        <ListTable
          loading={loading}
          dataSource={data?.results || []}
          columns={columns}
          rowKey="id"
          pagination={getTablePagination({
            current: page,
            pageSize: pageSizeOf(data, PHARMACY_PAGE_SIZE),
            total,
            onChange: (p) => setPage(p),
          })}
        />
      </ListCard>

      {/*
        * A return either starts blank or from this vendor's near-expiry
        * batches, so the choice is made before the form opens.
        */}
      <RaisePurchaseReturnModal
        open={raiseOpen}
        onClose={() => setRaiseOpen(false)}
        onStart={(prefill) =>
          navigate("/pharmacy/purchase-return/new", {
            state: prefill ? { purchaseReturnPrefill: prefill } : undefined,
          })
        }
      />
    </ListScreen>
  );
};

export default PurchaseReturnList;
